import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { type ActorForAnalyticsFull, toAssistantForAnalytics } from '@shared';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, EMPTY, Observable, retry } from 'rxjs';
import { environment } from '../../environments/environment';
import * as fromGenerated from '../_generated';
import * as fromAuth from '../auth/store/auth.reducer';
import { transcriptionChunksToCleanString } from '../chats/conversation/prompt-input/speech-to-text/speech-to-text.helper';
import * as fromShared from '../chats/shared';
import * as fromChats from '../chats/store/chats.reducer';
import type {
  ChatRenamedEvent,
  DeepgramApiErrorEvent,
  DeepgramConnectionLostEvent,
  DeepgramSttEvent,
  DocumentViewerOpenedEvent,
  ExportChatbotMessageEvent,
  FileDownloadedEvent,
  FileUploadedEvent,
  ImageAttachedEvent,
  ImageOverlayOpenedEvent,
  MessageSentEvent,
  MicrophoneAccessEvent,
  QuickActionSentEvent,
} from './analytics.events';
import * as fromHelper from './analytics.helper';
import {
  type ExponentialBackoffRetryOptions,
  createExponentialBackoffRetryConfig,
} from './analytics.helper';

/** Retry options for analytics POST (e.g. connection-lost events): 10 retries, 3s base delay, 60s cap. */
const ANALYTICS_POST_RETRY_OPTIONS: ExponentialBackoffRetryOptions = {
  count: 10,
  baseDelayMs: 3_000,
  capDelayMs: 60_000,
};

/**
 * Service responsible for sending analytics events and creating analytics payloads.
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly store = inject(Store);
  private readonly deviceService = inject(DeviceDetectorService);
  private readonly translateService = inject(TranslateService);
  private readonly heartbeatService = inject(fromGenerated.HeartbeatService);

  /**
   * Returns the context information for the analytics controller.
   */
  private get context() {
    return {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      env: environment.sentryOptions.environment!,
      version: environment.appReleaseVersion,
      deviceInfo: this.deviceService.deviceInfo(),
      currentLang: this.translateService.getCurrentLang(),
    };
  }

  /**
   * When logged in, include `actor` on the heartbeat payload; when not, omit the property.
   */
  private optionalActorPayload(
    user: fromGenerated.UserView | null,
  ): { actor: fromGenerated.AnalyticsActorDto } | Record<string, never> {
    if (!user) {
      return {};
    }
    return {
      actor: {
        userId: user.id,
        identityProviderId: user.identityProviderId,
        username: user.username,
        email: user.email,
        role: user.role,
      } satisfies ActorForAnalyticsFull,
    };
  }

  /**
   * Sends a document-viewer-opened event to the analytics controller.
   *
   * @param documentViewerOpenedEvent - The document viewer opened event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendDocumentViewerOpenedEvent(
    documentViewerOpenedEvent: DocumentViewerOpenedEvent,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 2,
        type: 'document_viewer_opened',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: documentViewerOpenedEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a message-sent event to the analytics controller.
   *
   * @param chatWithMessages - An instance of `ChatWithMessagesView` containing the chat details and associated messages.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendMessageSentEvent(
    chatWithMessages: fromGenerated.ChatWithMessagesView,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();
    const selectedChatAssistant = this.store.selectSignal(
      fromShared.selectSelectedChatAssistant,
    )();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 2,
        type: 'message_sent',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: {
          origin: selectedChatAssistant ? 'assistant' : 'chat',
          ...(selectedChatAssistant
            ? {
                assistant: toAssistantForAnalytics(selectedChatAssistant),
              }
            : {}),
          conversationId: chatWithMessages.id,
          startedConversation:
            fromHelper.isStartingNewConversation(chatWithMessages),
        } satisfies MessageSentEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  sendChatRenamedEvent(
    chatRenamedEvent: ChatRenamedEvent,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 2,
        type: 'chat_renamed',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: chatRenamedEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a quick-action-sent event to the analytics controller.
   *
   * @param chatWithMessages - An instance of `ChatWithMessagesView` containing the chat details and associated messages.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendQuickActionSentEvent(
    chatWithMessages: fromGenerated.ChatWithMessagesView,
    quickActionType: fromGenerated.RequestQuickActionDto.QuickActionEnum,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();
    const selectedChatAssistant = this.store.selectSignal(
      fromShared.selectSelectedChatAssistant,
    )();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 2,
        type: 'quick_action_sent',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: {
          origin: selectedChatAssistant ? 'assistant' : 'chat',
          ...(selectedChatAssistant
            ? {
                assistant: toAssistantForAnalytics(selectedChatAssistant),
              }
            : {}),
          conversationId: chatWithMessages.id,
          type: quickActionType,
        } satisfies QuickActionSentEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  sendExportChatbotMessageEvent(chatId: number, exportFormat: 'docx') {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();
    const selectedChatAssistant = this.store.selectSignal(
      fromShared.selectSelectedChatAssistant,
    )();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'export_chatbot_message',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: {
          origin: selectedChatAssistant ? 'assistant' : 'chat',
          ...(selectedChatAssistant
            ? {
                assistant: toAssistantForAnalytics(selectedChatAssistant),
              }
            : {}),
          conversationId: chatId,
          exportFormat,
        } satisfies ExportChatbotMessageEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a file-uploaded event to the analytics controller.
   *
   * @param fileUploadedEvent - The file uploaded event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendFileUploadedEvent(
    fileUploadedEvent: FileUploadedEvent,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'file_uploaded',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: fileUploadedEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends an image-attached event to the analytics controller.
   * Fired when an image is staged in the chat composer.
   * Metadata only — no image bytes leave the client (Art XI).
   *
   * @param imageAttachedEvent - The image attached event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendImageAttachedEvent(
    imageAttachedEvent: ImageAttachedEvent,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'image_attached',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: imageAttachedEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends an image-overlay-opened event to the analytics controller.
   * Fired when the full-screen image overlay opens.
   *
   * @param imageOverlayOpenedEvent - The image overlay opened event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendImageOverlayOpenedEvent(
    imageOverlayOpenedEvent: ImageOverlayOpenedEvent,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'image_overlay_opened',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: imageOverlayOpenedEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a file-downloaded event to the analytics controller.
   *
   * @param fileDownloadedEvent - The file downloaded event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendFileDownloadedEvent(
    fileDownloadedEvent: FileDownloadedEvent,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'file_downloaded',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: fileDownloadedEvent,
      })
      .pipe(
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a deepgram_stt event with API key generation error data to the analytics controller.
   * API key generation error events are sent as part of the deepgram_stt umbrella event type,
   * with API key generation error information in the properties field.
   *
   * @param deepgramApiKeyGenerationErrorEvent - The API key generation error event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendDeepgramApiKeyGenerationErrorEvent(
    deepgramApiKeyGenerationErrorEvent: Pick<
      DeepgramApiErrorEvent,
      'previousState' | 'currentState' | 'toastMessage' | 'transactionId'
    >,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();
    const selectedChatId = this.store.selectSignal(
      fromChats.chatsFeature.selectSelectedChatId,
    )();
    const selectedChatAssistant = this.store.selectSignal(
      fromShared.selectSelectedChatAssistant,
    )();

    const origin: 'chat' | 'assistant' = selectedChatAssistant
      ? 'assistant'
      : 'chat';

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'deepgram_stt',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: {
          previousState: deepgramApiKeyGenerationErrorEvent.previousState,
          currentState: deepgramApiKeyGenerationErrorEvent.currentState,
          reason: 'api_key_generation_error',
          transactionId: deepgramApiKeyGenerationErrorEvent.transactionId,
          origin,
          ...(selectedChatAssistant
            ? { assistant: toAssistantForAnalytics(selectedChatAssistant) }
            : {}),
          ...(selectedChatId ? { conversationId: selectedChatId } : {}),
          toastMessage: deepgramApiKeyGenerationErrorEvent.toastMessage,
        } satisfies DeepgramApiErrorEvent,
      })
      .pipe(
        /**
         * Retry with exponential backoff to handle network failures gracefully.
         * This is especially important when the user loses network connectivity,
         * as the analytics call may fail but should retry when connectivity is restored.
         * After max retries, the error is caught and logged to Sentry.
         */
        retry(
          createExponentialBackoffRetryConfig(ANALYTICS_POST_RETRY_OPTIONS),
        ),
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a deepgram_stt event with microphone access data to the analytics controller.
   * Microphone access events are sent as part of the deepgram_stt umbrella event type,
   * with microphone access information in the properties field.
   *
   * @param microphoneAccessEvent - The microphone access event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendDeepgramMicrophoneAccessEvent(
    microphoneAccessEvent: Pick<
      MicrophoneAccessEvent,
      | 'previousState'
      | 'currentState'
      | 'reason'
      | 'toastMessage'
      | 'transactionId'
    >,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();
    const selectedChatId = this.store.selectSignal(
      fromChats.chatsFeature.selectSelectedChatId,
    )();
    const selectedChatAssistant = this.store.selectSignal(
      fromShared.selectSelectedChatAssistant,
    )();

    const origin: 'chat' | 'assistant' = selectedChatAssistant
      ? 'assistant'
      : 'chat';

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'deepgram_stt',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: {
          previousState: microphoneAccessEvent.previousState,
          currentState: microphoneAccessEvent.currentState,
          reason: microphoneAccessEvent.reason,
          transactionId: microphoneAccessEvent.transactionId,
          origin,
          ...(selectedChatAssistant
            ? { assistant: toAssistantForAnalytics(selectedChatAssistant) }
            : {}),
          ...(selectedChatId ? { conversationId: selectedChatId } : {}),
          ...(microphoneAccessEvent.toastMessage
            ? { toastMessage: microphoneAccessEvent.toastMessage }
            : {}),
        } satisfies MicrophoneAccessEvent,
      })
      .pipe(
        /**
         * Retry with exponential backoff to handle network failures gracefully.
         * This is especially important when the user loses network connectivity,
         * as the analytics call may fail but should retry when connectivity is restored.
         * After max retries, the error is caught and logged to Sentry.
         */
        retry(
          createExponentialBackoffRetryConfig(ANALYTICS_POST_RETRY_OPTIONS),
        ),
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a deepgram-stt event to the analytics controller.
   *
   * @param event - The STT event data including:
   *   - `previousState`: State before the transition
   *   - `currentState`: State after the transition
   *   - `reason`: Reason for the state transition or error
   *   - `transactionId`: Unique UUID tracking the STT flow
   *   - `connectionStartTime`: Timestamp when connection started (for duration calculation)
   *   - `transcriptionChunks`: Array of transcription chunks (for word count calculation)
   *   - `currentSessionConnectivityDrops`: Optional count of connectivity drops (online → offline transitions) during the session
   *   - `toastMessage`: Optional toast message shown to the user (for error events)
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendDeepgramSttEvent(
    event: Pick<
      DeepgramSttEvent,
      | 'previousState'
      | 'currentState'
      | 'reason'
      | 'toastMessage'
      | 'transactionId'
      | 'currentSessionConnectivityDrops'
    > & {
      connectionStartTime: number;
      transcriptionChunks: string[];
    },
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();
    const selectedChatId = this.store.selectSignal(
      fromChats.chatsFeature.selectSelectedChatId,
    )();
    const selectedChatAssistant = this.store.selectSignal(
      fromShared.selectSelectedChatAssistant,
    )();

    const origin: 'chat' | 'assistant' = selectedChatAssistant
      ? 'assistant'
      : 'chat';

    // Calculate connection duration when appropriate (completed, finalizing, aborting, error states)
    const connectionDurationMs =
      event.connectionStartTime > 0 &&
      (event.currentState === 'completed' ||
        event.currentState === 'finalizing' ||
        event.currentState === 'aborting' ||
        event.currentState === 'error')
        ? Date.now() - event.connectionStartTime
        : undefined;

    // Calculate word count when appropriate (completed, finalizing, aborting, error states)
    let numberOfWords: number | undefined;
    if (
      event.currentState === 'completed' ||
      event.currentState === 'finalizing' ||
      event.currentState === 'aborting' ||
      event.currentState === 'error'
    ) {
      // Use the same cleaning logic as the transcription display for accurate word count
      const transcriptionText = transcriptionChunksToCleanString(
        event.transcriptionChunks,
      );
      // Simple word count: split by whitespace and filter out empty strings
      numberOfWords =
        transcriptionText.length > 0
          ? transcriptionText.split(/\s+/).filter(Boolean).length
          : 0;
    }

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'deepgram_stt',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: {
          previousState: event.previousState,
          currentState: event.currentState,
          reason: event.reason,
          transactionId: event.transactionId,
          origin,
          ...(selectedChatAssistant
            ? { assistant: toAssistantForAnalytics(selectedChatAssistant) }
            : {}),
          ...(selectedChatId ? { conversationId: selectedChatId } : {}),
          ...(connectionDurationMs !== undefined
            ? { connectionDurationMs }
            : {}),
          ...(numberOfWords !== undefined ? { numberOfWords } : {}),
          ...(event.currentSessionConnectivityDrops !== undefined
            ? {
                currentSessionConnectivityDrops:
                  event.currentSessionConnectivityDrops,
              }
            : {}),
          ...(event.toastMessage ? { toastMessage: event.toastMessage } : {}),
        } satisfies DeepgramSttEvent,
      })
      .pipe(
        /**
         * Retry with exponential backoff to handle network failures gracefully.
         * This is especially important when the user loses network connectivity,
         * as the analytics call may fail but should retry when connectivity is restored.
         * After max retries, the error is caught and logged to Sentry.
         */
        retry(
          createExponentialBackoffRetryConfig(ANALYTICS_POST_RETRY_OPTIONS),
        ),
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }

  /**
   * Sends a deepgram_stt event with connection lost data to the analytics controller.
   * Connection lost events are sent as part of the deepgram_stt umbrella event type,
   * with connection loss information in the properties field.
   *
   * @param connectionLostEvent - The connection lost event data.
   * @return Observable that emits the response from the analytics controller when the event is successfully sent.
   */
  sendDeepgramConnectionLostEvent(
    connectionLostEvent: Omit<
      DeepgramConnectionLostEvent,
      'origin' | 'assistant' | 'conversationId'
    >,
  ): Observable<unknown> {
    const user = this.store.selectSignal(fromAuth.authFeature.selectUser)();
    const selectedChatId = this.store.selectSignal(
      fromChats.chatsFeature.selectSelectedChatId,
    )();
    const selectedChatAssistant = this.store.selectSignal(
      fromShared.selectSelectedChatAssistant,
    )();

    const origin: 'chat' | 'assistant' = selectedChatAssistant
      ? 'assistant'
      : 'chat';

    return this.heartbeatService
      .analyticsControllerPostAnalyticV1({
        schemaVersion: 1,
        type: 'deepgram_stt',
        session: fromHelper.createSessionInfo(),
        ...this.optionalActorPayload(user),
        context: fromHelper.createContextInfo(this.context),
        properties: {
          ...connectionLostEvent,
          origin,
          ...(selectedChatId ? { conversationId: selectedChatId } : {}),
          ...(selectedChatAssistant
            ? { assistant: toAssistantForAnalytics(selectedChatAssistant) }
            : {}),
        } satisfies DeepgramConnectionLostEvent,
      })
      .pipe(
        /**
         * Retry with exponential backoff to handle network failures gracefully.
         * This is especially important when the user loses network connectivity,
         * as the analytics call may fail but should retry when connectivity is restored.
         * After max retries, the error is caught and logged to Sentry.
         */
        retry(
          createExponentialBackoffRetryConfig(ANALYTICS_POST_RETRY_OPTIONS),
        ),
        catchError((error) => {
          Sentry.captureException(error);
          return EMPTY;
        }),
      );
  }
}
